/**
 * ADR-0223 §3.3 — Ideas (`/app/collab/ideas`): the four-column board over
 * every idea, twelve per column, with the list view past twelve
 * (`?state=<state>&view=list`, a guideline-conformant table). A text filter
 * sits above the board.
 */

import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { apiFetch } from "../lib/api.js";
import { ErrorState, LoadingState } from "../components/States.js";
import { Board, CollabPage, errMessage, IDEA_STATES, shortDate, type IdeaCard, type IdeaState, type Week } from "../components/collab.js";

const COLUMN_LABELS: Record<IdeaState, string> = { open: "Open", blocked: "Blocked", became: "Became something", aside: "Set aside" };

/** Pure, for the tests: cards grouped into the four columns, filtered by the text box. */
export function groupIdeas(ideas: IdeaCard[], q: string): Week["ideas"] {
  const needle = q.trim().toLowerCase();
  const kept = needle ? ideas.filter((i) => i.title.toLowerCase().includes(needle)) : ideas;
  return Object.fromEntries(
    IDEA_STATES.map((s) => {
      const items = kept.filter((i) => i.state === s);
      return [s, { label: COLUMN_LABELS[s], count: items.length, items }];
    })
  ) as Week["ideas"];
}

export function CollabIdeas() {
  const [params, setParams] = useSearchParams();
  const state = params.get("state");
  const view = params.get("view");
  const [ideas, setIdeas] = useState<IdeaCard[] | null>(null);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    void apiFetch<{ ideas: IdeaCard[] }>("/api/tenant/collab/ideas")
      .then((r) => {
        if (!cancelled) setIdeas(r.ideas);
      })
      .catch((e) => {
        if (!cancelled) setError(errMessage(e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const grouped = useMemo(() => groupIdeas(ideas ?? [], q), [ideas, q]);
  const listState: IdeaState | null = view === "list" && state && (IDEA_STATES as string[]).includes(state) ? (state as IdeaState) : null;

  return (
    <CollabPage title="Ideas">
      {error ? <ErrorState detail={error} /> : null}
      {!error && loading ? <LoadingState /> : null}
      {!error && !loading && ideas ? (
        listState ? (
          <IdeaListView state={listState} ideas={grouped[listState].items} onBack={() => setParams({})} />
        ) : (
          <>
            <div className="collabHead">
              <div className="collabKicker">Atlas Collab</div>
              <input className="collabInput" type="search" placeholder="Filter ideas by title" aria-label="Filter ideas" value={q} onChange={(e) => setQ(e.target.value)} style={{ maxWidth: 420 }} />
            </div>
            <Board
              ideas={grouped}
              limit={12}
              more={(s, count) => (
                <Link className="collabLinkBtn" to={`/app/collab/ideas?state=${s}&view=list`} style={{ justifySelf: "start" }}>
                  Show all {count}
                </Link>
              )}
            />
          </>
        )
      ) : null}
    </CollabPage>
  );
}

/** The list view (§3.3): a guideline table — Idea · Raised by · Raised · Came from · Discussed · Chatter — quiet ideas last (the hub sorts them). */
export function IdeaListView(props: { state: IdeaState; ideas: IdeaCard[]; onBack: () => void }) {
  return (
    <div className="collabListWrap">
      <div className="collabSecHead" style={{ padding: "12px 4px 0" }}>
        <h2 className="collabH2">{COLUMN_LABELS[props.state]}</h2>
        <span className="count">{props.ideas.length}</span>
        <button type="button" className="collabLinkBtn more" onClick={props.onBack}>
          Back to the board
        </button>
      </div>
      <div className="card adminList tableWrap" style={{ boxShadow: "none" }}>
        <table className="table">
          <thead>
            <tr>
              <th>Idea</th>
              <th>Raised by</th>
              <th>Raised</th>
              <th>Came from</th>
              <th>Discussed</th>
              <th>Chatter</th>
            </tr>
          </thead>
          <tbody>
            {props.ideas.length === 0 ? (
              <tr>
                <td colSpan={6} className="adminListEmpty">
                  None
                </td>
              </tr>
            ) : null}
            {props.ideas.map((i) => (
              <tr key={i.id}>
                <td>
                  <Link className="adminListName" to={`/app/collab/ideas/${i.id}`}>
                    {i.title}
                  </Link>
                  {i.quiet ? (
                    <span className="collabQuiet" style={{ marginLeft: 8 }}>
                      Quiet
                    </span>
                  ) : null}
                </td>
                <td>{i.raised_by?.name ?? <span className="muted">—</span>}</td>
                <td>{shortDate(i.raised_on)}</td>
                <td>{i.came_from.session_id ? <Link to={`/app/collab/sessions/${i.came_from.session_id}`}>{i.came_from.label}</Link> : i.came_from.label}</td>
                <td>{i.discussed}</td>
                <td>{i.chatter_count}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
